import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import MuiInputTextWithRemainingChars from '@shared/components/MuiInputTextWithRemainingChars';
import { MIN_LENGTH, MAX_LENGTH } from '../../../../constants/EventCommentsConstants';
import { joiResolver } from '@hookform/resolvers/joi';
import { Button } from '@mui/material';
import EventCommentValidator from '../../../../validators/EventCommentValidator';
import _ from 'lodash';
import ErrorMessage from '@shared/components/ErrorMessage';
import CustomSelector from '@shared/components/CustomSelector';

/**
 * @typedef {object} CommentFormValues
 * @property {number|null} Id
 * @property {string} CommentTarget
 * @property {string} Comment
 */

/**
 * @typedef {object} CommentInputProps
 * @property {Array<*>} [accessibilityOptions]
 * @property {(data: { Id: number | null, CommentTarget: string, Comment: string }) => void | Promise<void>} saveComment
 * @property {() => void} onCancel
 * @property {Partial<CommentFormValues>} [comment]
 * @property {string} [successMessage]
 */

/**
 * @param {CommentInputProps} props
 */
const CommentInput = props => {
  const { comment = {}, accessibilityOptions = [], saveComment, onCancel } = props;

  const form = useForm({
    resolver: joiResolver(EventCommentValidator.schema),
    defaultValues: {
      Id: null,
      CommentTarget: '',
      Comment: ''
    },
    values: comment
  });

  const {
    formState: { errors },
    handleSubmit,
    control,
    reset
  } = form;

  const onSubmit = async data => {
    await saveComment(data);
    reset();
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const commentTargetError = errors.CommentTarget?.message;
  const commentError = errors.Comment?.message;

  return (
    <form className='comment-input'>
      <FormProvider {...form}>
        <div className='input-header'>
          <div className='label bold'>Who can see your comment</div>

          <Controller
            control={control}
            name='CommentTarget'
            render={({ field }) => (
              <CustomSelector
                name='CommentTarget'
                id='roles-selector'
                placeholder='Select a User Role'
                testId='roles-selector'
                options={accessibilityOptions}
                error={hasError(errors, 'CommentTarget')}
                value={field.value}
                setValue={field.onChange}
              />
            )}
          />
        </div>

        <div className='input-body'>
          <div className='label bold'>Comment</div>

          <div className='input-area'>
            <Controller
              control={control}
              name='Comment'
              render={({ field }) => (
                <MuiInputTextWithRemainingChars
                  name='Comment'
                  className='value'
                  error={hasError(errors, 'Comment')}
                  disableUnderline
                  multiline={true}
                  rows={5}
                  value={field.value}
                  onChange={field.onChange}
                  minLength={MIN_LENGTH}
                  maxLength={MAX_LENGTH}
                  hideRemainingChars={false}
                  placeholder='Enter Comment'
                />
              )}
            />
          </div>

          <div>
            <div className='action-buttons'>
              <Button className='secondary-button sm' onClick={handleCancel}>
                Cancel
              </Button>

              <Button className='primary-button sm' onClick={handleSubmit(onSubmit)}>
                Save
              </Button>
            </div>

            {commentTargetError && (
              <ErrorMessage className='error-message' message={commentTargetError} />
            )}
            {commentError && <ErrorMessage className='error-message' message={commentError} />}
          </div>
        </div>
      </FormProvider>
    </form>
  );
};

const hasError = (errors, fieldName) => Boolean(_.get(errors, fieldName));

export default CommentInput;
